import i18next from 'i18next'
import { initReactI18next } from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/en'
import 'dayjs/locale/cs'
import 'dayjs/locale/sk'

import enAlerts from './locales/en/alerts.json'
import enBilling from './locales/en/billing.json'
import enButtons from './locales/en/buttons.json'
import enChangePassword from './locales/en/changePassword.json'
import enCommon from './locales/en/common.json'
import enControlData from './locales/en/controlData.json'
import enDashboard from './locales/en/dashboard.json'
import enDataSync from './locales/en/dataSync.json'
import enDetectionComparison from './locales/en/detectionComparison.json'
import enDiagnostics from './locales/en/diagnostics.json'
import enDocumentation from './locales/en/documentation.json'
import enDriveDetails from './locales/en/driveDetails.json'
import enDriveDetailsTooltips from './locales/en/driveDetailsTooltips.json'
import enDrivers from './locales/en/drivers.json'
import enErrors from './locales/en/errors.json'
import enHardware from './locales/en/hardware.json'
import enHotline from './locales/en/hotline.json'
import enLogin from './locales/en/login.json'
import enManagementLayout from './locales/en/managementLayout.json'
import enMapSnapshots from './locales/en/mapSnapshots.json'
import enNav from './locales/en/nav.json'
import enNavigation from './locales/en/navigation.json'
import enOthers from './locales/en/others.json'
import enRouteManagement from './locales/en/routeManagement.json'
import enRouteProblems from './locales/en/routeProblems.json'
import enRoutePlanner from './locales/en/routePlanner.json'
import enScheduling from './locales/en/scheduling.json'
import enShifts from './locales/en/shifts.json'
import enStatistics from './locales/en/statistics.json'
import enUnexpectedEvents from './locales/en/unexpectedEvents.json'
import enVehicles from './locales/en/vehicles.json'

import csAlerts from './locales/cs/alerts.json'
import csBilling from './locales/cs/billing.json'
import csButtons from './locales/cs/buttons.json'
import csChangePassword from './locales/cs/changePassword.json'
import csCommon from './locales/cs/common.json'
import csControlData from './locales/cs/controlData.json'
import csDashboard from './locales/cs/dashboard.json'
import csDataSync from './locales/cs/dataSync.json'
import csDetectionComparison from './locales/cs/detectionComparison.json'
import csDiagnostics from './locales/cs/diagnostics.json'
import csDocumentation from './locales/cs/documentation.json'
import csDriveDetails from './locales/cs/driveDetails.json'
import csDriveDetailsTooltips from './locales/cs/driveDetailsTooltips.json'
import csDrivers from './locales/cs/drivers.json'
import csErrors from './locales/cs/errors.json'
import csHardware from './locales/cs/hardware.json'
import csHotline from './locales/cs/hotline.json'
import csLogin from './locales/cs/login.json'
import csManagementLayout from './locales/cs/managementLayout.json'
import csMapSnapshots from './locales/cs/mapSnapshots.json'
import csNav from './locales/cs/nav.json'
import csNavigation from './locales/cs/navigation.json'
import csOthers from './locales/cs/others.json'
import csRouteManagement from './locales/cs/routeManagement.json'
import csRouteProblems from './locales/cs/routeProblems.json'
import csRoutePlanner from './locales/cs/routePlanner.json'
import csScheduling from './locales/cs/scheduling.json'
import csShifts from './locales/cs/shifts.json'
import csStatistics from './locales/cs/statistics.json'
import csUnexpectedEvents from './locales/cs/unexpectedEvents.json'
import csVehicles from './locales/cs/vehicles.json'

import skAlerts from './locales/sk/alerts.json'
import skBilling from './locales/sk/billing.json'
import skButtons from './locales/sk/buttons.json'
import skChangePassword from './locales/sk/changePassword.json'
import skCommon from './locales/sk/common.json'
import skControlData from './locales/sk/controlData.json'
import skDashboard from './locales/sk/dashboard.json'
import skDataSync from './locales/sk/dataSync.json'
import skDetectionComparison from './locales/sk/detectionComparison.json'
import skDiagnostics from './locales/sk/diagnostics.json'
import skDocumentation from './locales/sk/documentation.json'
import skDriveDetails from './locales/sk/driveDetails.json'
import skDriveDetailsTooltips from './locales/sk/driveDetailsTooltips.json'
import skDrivers from './locales/sk/drivers.json'
import skErrors from './locales/sk/errors.json'
import skHardware from './locales/sk/hardware.json'
import skHotline from './locales/sk/hotline.json'
import skLogin from './locales/sk/login.json'
import skManagementLayout from './locales/sk/managementLayout.json'
import skMapSnapshots from './locales/sk/mapSnapshots.json'
import skNav from './locales/sk/nav.json'
import skNavigation from './locales/sk/navigation.json'
import skOthers from './locales/sk/others.json'
import skRouteManagement from './locales/sk/routeManagement.json'
import skRouteProblems from './locales/sk/routeProblems.json'
import skRoutePlanner from './locales/sk/routePlanner.json'
import skScheduling from './locales/sk/scheduling.json'
import skShifts from './locales/sk/shifts.json'
import skStatistics from './locales/sk/statistics.json'
import skUnexpectedEvents from './locales/sk/unexpectedEvents.json'
import skVehicles from './locales/sk/vehicles.json'

const LANGUAGE_STORAGE_KEY = 'city-sense-language'

const getStoredLanguage = (): string => {
  try {
    return localStorage.getItem(LANGUAGE_STORAGE_KEY) ?? 'cs'
  } catch {
    return 'cs'
  }
}

const NAMESPACES = [
  'alerts',
  'billing',
  'buttons',
  'changePassword',
  'common',
  'controlData',
  'dashboard',
  'dataSync',
  'detectionComparison',
  'diagnostics',
  'documentation',
  'driveDetails',
  'driveDetailsTooltips',
  'drivers',
  'errors',
  'hardware',
  'hotline',
  'login',
  'managementLayout',
  'mapSnapshots',
  'nav',
  'navigation',
  'others',
  'routeManagement',
  'routeProblems',
  'routePlanner',
  'scheduling',
  'shifts',
  'statistics',
  'unexpectedEvents',
  'vehicles',
] as const

i18next.use(initReactI18next).init({
  lng: getStoredLanguage(),
  fallbackLng: 'en',
  supportedLngs: ['en', 'cs', 'sk'],
  ns: [...NAMESPACES],
  defaultNS: 'common',
  react: { useSuspense: false },
  interpolation: { escapeValue: false },
  resources: {
    en: {
      alerts: enAlerts,
      billing: enBilling,
      buttons: enButtons,
      changePassword: enChangePassword,
      common: enCommon,
      controlData: enControlData,
      dashboard: enDashboard,
      dataSync: enDataSync,
      detectionComparison: enDetectionComparison,
      diagnostics: enDiagnostics,
      documentation: enDocumentation,
      driveDetails: enDriveDetails,
      driveDetailsTooltips: enDriveDetailsTooltips,
      drivers: enDrivers,
      errors: enErrors,
      hardware: enHardware,
      hotline: enHotline,
      login: enLogin,
      managementLayout: enManagementLayout,
      mapSnapshots: enMapSnapshots,
      nav: enNav,
      navigation: enNavigation,
      others: enOthers,
      routeManagement: enRouteManagement,
      routeProblems: enRouteProblems,
      routePlanner: enRoutePlanner,
      scheduling: enScheduling,
      shifts: enShifts,
      statistics: enStatistics,
      unexpectedEvents: enUnexpectedEvents,
      vehicles: enVehicles,
    },
    cs: {
      alerts: csAlerts,
      billing: csBilling,
      buttons: csButtons,
      changePassword: csChangePassword,
      common: csCommon,
      controlData: csControlData,
      dashboard: csDashboard,
      dataSync: csDataSync,
      detectionComparison: csDetectionComparison,
      diagnostics: csDiagnostics,
      documentation: csDocumentation,
      driveDetails: csDriveDetails,
      driveDetailsTooltips: csDriveDetailsTooltips,
      drivers: csDrivers,
      errors: csErrors,
      hardware: csHardware,
      hotline: csHotline,
      login: csLogin,
      managementLayout: csManagementLayout,
      mapSnapshots: csMapSnapshots,
      nav: csNav,
      navigation: csNavigation,
      others: csOthers,
      routeManagement: csRouteManagement,
      routeProblems: csRouteProblems,
      routePlanner: csRoutePlanner,
      scheduling: csScheduling,
      shifts: csShifts,
      statistics: csStatistics,
      unexpectedEvents: csUnexpectedEvents,
      vehicles: csVehicles,
    },
    sk: {
      alerts: skAlerts,
      billing: skBilling,
      buttons: skButtons,
      changePassword: skChangePassword,
      common: skCommon,
      controlData: skControlData,
      dashboard: skDashboard,
      dataSync: skDataSync,
      detectionComparison: skDetectionComparison,
      diagnostics: skDiagnostics,
      documentation: skDocumentation,
      driveDetails: skDriveDetails,
      driveDetailsTooltips: skDriveDetailsTooltips,
      drivers: skDrivers,
      errors: skErrors,
      hardware: skHardware,
      hotline: skHotline,
      login: skLogin,
      managementLayout: skManagementLayout,
      mapSnapshots: skMapSnapshots,
      nav: skNav,
      navigation: skNavigation,
      others: skOthers,
      routeManagement: skRouteManagement,
      routeProblems: skRouteProblems,
      routePlanner: skRoutePlanner,
      scheduling: skScheduling,
      shifts: skShifts,
      statistics: skStatistics,
      unexpectedEvents: skUnexpectedEvents,
      vehicles: skVehicles,
    },
  },
})

// Keep dayjs locale in sync with i18next so date formatting (month names,
// weekday names, etc.) follows the active UI language.
dayjs.locale(getStoredLanguage())

i18next.on('languageChanged', (lng: string) => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng)
  } catch {
    // localStorage unavailable (e.g. private browsing); ignore
  }
  dayjs.locale(lng)
})

export default i18next
